import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { payrollAPI } from '../../services/api';
import { useToast } from '../Toast';
import PayrollGenerateModal from './PayrollGenerateModal';
import PayrollPayModal from './PayrollPayModal';
import PayrollEditModal from './PayrollEditModal';
import {
  PlusIcon,
  CheckCircleIcon,
  CurrencyRupeeIcon,
  EnvelopeIcon,
  TrashIcon,
  DocumentCheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

const EmployeePayroll = ({ employeeId, employeeName, salary }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  // Fetch payroll history for this employee
  const { data: payrollData, isLoading } = useQuery({
    queryKey: ['employeePayroll', employeeId],
    queryFn: async () => {
      const response = await payrollAPI.getByEmployee(employeeId, 0, 24);
      return response.data;
    },
  });

  // Finalize mutation
  const finalizeMutation = useMutation({
    mutationFn: (payrollId) => payrollAPI.finalize(payrollId),
    onSuccess: () => {
      queryClient.invalidateQueries(['employeePayroll', employeeId]);
      showToast('Payroll finalized successfully', 'success');
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to finalize payroll', 'error');
    },
  });

  // Send salary slip mutation
  const sendSlipMutation = useMutation({
    mutationFn: (payrollId) => payrollAPI.sendSalarySlip(payrollId),
    onSuccess: () => {
      showToast('Salary slip sent successfully', 'success');
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to send salary slip', 'error');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (payrollId) => payrollAPI.delete(payrollId),
    onSuccess: () => {
      queryClient.invalidateQueries(['employeePayroll', employeeId]);
      showToast('Payroll deleted successfully', 'success');
    },
    onError: (error) => {
      showToast(error.response?.data?.detail || 'Failed to delete payroll', 'error');
    },
  });

  const handleFinalize = (payroll) => {
    if (window.confirm(`Are you sure you want to finalize payroll for ${getMonthName(payroll.month)} ${payroll.year}?`)) {
      finalizeMutation.mutate(payroll.payroll_id);
    }
  };

  const handleMarkAsPaid = (payroll) => {
    setSelectedPayroll(payroll);
    setShowPayModal(true);
  };

  const handleEdit = (payroll) => {
    setSelectedPayroll(payroll);
    setShowEditModal(true);
  };

  const handleSendSlip = (payroll) => {
    if (window.confirm(`Send salary slip to ${employeeName}?`)) {
      sendSlipMutation.mutate(payroll.payroll_id);
    }
  };

  const handleDelete = (payroll) => {
    if (window.confirm(`Are you sure you want to delete this payroll? This action cannot be undone.`)) {
      deleteMutation.mutate(payroll.payroll_id);
    }
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-900/50 text-yellow-400 border border-yellow-500">
            Draft
          </span>
        );
      case 'finalized':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-900/50 text-blue-400 border border-blue-500">
            Finalized
          </span>
        );
      case 'paid':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-900/50 text-green-400 border border-green-500">
            Paid
          </span>
        );
      default:
        return null;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Generate Button */}
      <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center">
            <CurrencyRupeeIcon className="w-5 h-5 mr-2 text-green-500" />
            Payroll History
          </h2>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Generate Payroll</span>
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Monthly Salary</p>
            <p className="text-xl font-semibold text-white">{formatCurrency(salary)}</p>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Total Records</p>
            <p className="text-xl font-semibold text-white">{payrollData?.length || 0}</p>
          </div>
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400">Paid Records</p>
            <p className="text-xl font-semibold text-green-400">
              {payrollData?.filter(p => p.status === 'paid').length || 0}
            </p>
          </div>
        </div>

        {/* Payroll Table */}
        {!payrollData || payrollData.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <CurrencyRupeeIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No payroll records found</p>
            <p className="text-sm mt-2">Click "Generate Payroll" to create the first payroll</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-4 px-6 text-base font-medium text-gray-400">Period</th>
                  <th className="text-left py-4 px-6 text-base font-medium text-gray-400">Days</th>
                  <th className="text-right py-4 px-6 text-base font-medium text-gray-400">Gross</th>
                  <th className="text-right py-4 px-6 text-base font-medium text-gray-400">Deductions</th>
                  <th className="text-right py-4 px-6 text-base font-medium text-gray-400">Net</th>
                  <th className="text-center py-4 px-6 text-base font-medium text-gray-400">Status</th>
                  <th className="text-right py-4 px-6 text-base font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payrollData.map((payroll) => (
                  <tr key={payroll.payroll_id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-4 px-6">
                      <div>
                        <p className="text-white font-medium text-base">
                          {getMonthName(payroll.month)} {payroll.year}
                        </p>
                        {payroll.is_prorated && (
                          <p className="text-sm text-yellow-400">Pro-rata ({payroll.prorate_days} days)</p>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-300 text-base">
                      {payroll.days_worked} / {payroll.working_days_in_month}
                      {payroll.unpaid_leaves > 0 && (
                        <span className="text-sm text-red-400 ml-2">
                          (-{payroll.unpaid_leaves} unpaid)
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-right text-white text-base">
                      {formatCurrency(payroll.gross_salary)}
                    </td>
                    <td className="py-4 px-6 text-right text-red-400 text-base">
                      {payroll.leave_deduction > 0 ? `-${formatCurrency(payroll.leave_deduction)}` : '-'}
                    </td>
                    <td className="py-4 px-6 text-right text-green-400 font-semibold text-base">
                      {formatCurrency(payroll.net_salary)}
                    </td>
                    <td className="py-4 px-6 text-center">
                      {getStatusBadge(payroll.status)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end space-x-3">
                        {payroll.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleEdit(payroll)}
                              className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/30 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <PencilSquareIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleFinalize(payroll)}
                              disabled={finalizeMutation.isPending}
                              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Finalize"
                            >
                              <DocumentCheckIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(payroll)}
                              disabled={deleteMutation.isPending}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {payroll.status === 'finalized' && (
                          <button
                            onClick={() => handleMarkAsPaid(payroll)}
                            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/30 rounded-lg transition-colors"
                            title="Mark as Paid"
                          >
                            <CheckCircleIcon className="w-5 h-5" />
                          </button>
                        )}
                        {(payroll.status === 'finalized' || payroll.status === 'paid') && (
                          <button
                            onClick={() => handleSendSlip(payroll)}
                            disabled={sendSlipMutation.isPending}
                            className="p-2 text-purple-400 hover:text-purple-300 hover:bg-purple-900/30 rounded-lg transition-colors"
                            title="Send Salary Slip"
                          >
                            <EnvelopeIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <PayrollGenerateModal
          employeeId={employeeId}
          employeeName={employeeName}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={() => {
            setShowGenerateModal(false);
            queryClient.invalidateQueries(['employeePayroll', employeeId]);
          }}
        />
      )}

      {/* Pay Modal */}
      {showPayModal && selectedPayroll && (
        <PayrollPayModal
          payroll={selectedPayroll}
          onClose={() => {
            setShowPayModal(false);
            setSelectedPayroll(null);
          }}
          onSuccess={() => {
            setShowPayModal(false);
            setSelectedPayroll(null);
            queryClient.invalidateQueries(['employeePayroll', employeeId]);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPayroll && (
        <PayrollEditModal
          payroll={selectedPayroll}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPayroll(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedPayroll(null);
            queryClient.invalidateQueries(['employeePayroll', employeeId]);
          }}
        />
      )}
    </div>
  );
};

export default EmployeePayroll;
